import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, Switch, Input, Button, 
  message, Typography, Space, Divider, 
  Alert, Form 
} from 'antd';
import { 
  Send, Bot, Settings, Bell, 
  TrendingUp, Users, DollarSign, 
  AlertTriangle, RefreshCw, Save, PieChart 
} from 'lucide-react';
import api from '../../services/api';
import { motion } from 'framer-motion';

const { Title, Text } = Typography;

const MarketingManagement = () => {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [signalLoading, setSignalLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [form] = Form.useForm();
  const [signalForm] = Form.useForm();
  const [analysisForm] = Form.useForm();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/marketing/settings');
      if (response.success) {
        const formData = {};
        response.data.forEach(s => {
          formData[s.setting_key] = s.setting_type === 'boolean' 
            ? s.setting_value === 'true' 
            : s.setting_value;
        });
        form.setFieldsValue(formData);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('Impossible de charger les paramètres marketing');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = Object.keys(values).map(key => ({
        setting_key: key,
        setting_value: values[key] === true ? 'true' : (values[key] === false ? 'false' : (values[key] ?? ''))
      }));

      const response = await api.put('/admin/marketing/settings', payload);
      if (response.success) {
        message.success('Paramètres mis à jour avec succès');
        fetchSettings();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const sendTestMessage = async (type) => {
    setTestLoading(true);
    try {
      const response = await api.post('/admin/marketing/test-message', {
        channel_type: type,
        message: `🔔 TEST: Le système de notification ${type} est opérationnel !`
      });
      if (response.success) {
        message.success(`Message de test envoyé au canal ${type}`);
      }
    } catch (error) {
      message.error('Échec de l\'envoi du test. Vérifiez la configuration.');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSendSignal = async (values) => {
    setSignalLoading(true);
    try {
      const response = await api.post('/admin/marketing/signal', {
        ...values,
        targets: values.targets.split(',').map(t => t.trim())
      });
      if (response.success) {
        message.success('📊 Signal de trading envoyé sur Telegram !');
        signalForm.resetFields();
      } else {
        message.error('Erreur lors de l\'envoi du signal');
      }
    } catch (error) {
      console.error('Signal error:', error);
      message.error('Erreur lors de l\'envoi du signal');
    } finally {
      setSignalLoading(false);
    }
  };

  const handleSendAnalysis = async (values) => {
    setAnalysisLoading(true);
    try {
      const response = await api.post('/admin/marketing/analysis', values);
      if (response.success) {
        message.success('📈 Analyse de marché envoyée sur Telegram !');
        analysisForm.resetFields();
      } else {
        message.error('Erreur lors de l\'envoi de l\'analyse');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      message.error('Erreur lors de l\'envoi de l\'analyse');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-6"
    >
      <div className="flex justify-between items-center mb-8">
        <div>
          <Title level={2} className="!mb-1 !text-white flex items-center gap-3">
            <Bot size={32} className="text-indigo-400" />
            Marketing & Telegram Hub
          </Title>
          <Text className="text-slate-400">Gérez vos bots, vos notifications de preuve sociale et vos alertes admin.</Text>
        </div>
        <Button 
          icon={<RefreshCw size={16} />} 
          onClick={fetchSettings}
          className="flex items-center gap-2"
        >
          Actualiser
        </Button>
      </div>

      {/* === FORM 1: Settings (Token + Automatisations) === */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        className="space-y-8"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Telegram Core Settings */}
          <Card 
            title={<span className="flex items-center gap-2 text-indigo-400"><Settings size={18} /> Configuration API Telegram</span>}
            className="trading-card"
          >
            <Form.Item 
              name="tg_bot_token" 
              label="Token du Bot Telegram"
              help="Obtenez-le via @BotFather"
            >
              <Input.Password placeholder="123456789:ABCDEF..." className="terminal-input" autoComplete="new-password" />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item 
                name="tg_public_channel_id" 
                label="ID Canal Public (Social Proof)"
                help="Ex: -100123456789"
              >
                <Input placeholder="-100..." className="terminal-input" />
              </Form.Item>
            </div>

            <Divider className="border-white/5" />
            
            <Space className="w-full justify-between">
              <Button 
                onClick={() => sendTestMessage('public')}
                loading={testLoading}
                className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/20"
              >
                Tester Canal Public
              </Button>
            </Space>
          </Card>

          {/* Marketing Automation Toggles */}
          <Card 
            title={<span className="flex items-center gap-2 text-emerald-400"><Bell size={18} /> Automatisations Marketing</span>}
            className="trading-card"
          >
            <div className="space-y-6">
              <div className="flex justify-between items-center p-3 bg-slate-950/30 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
                    <Users size={20} />
                  </div>
                  <div>
                    <Text className="block font-bold">Bienvenue Nouveaux Membres</Text>
                    <Text size="small" className="text-slate-500 text-xs">Annonce chaque inscription sur le canal public.</Text>
                  </div>
                </div>
                <Form.Item name="marketing_enable_welcome_tg" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <div className="flex justify-between items-center p-3 bg-slate-950/30 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-400">
                    <DollarSign size={20} />
                  </div>
                  <div>
                    <Text className="block font-bold">Preuve de Paiement</Text>
                    <Text size="small" className="text-slate-500 text-xs">Affiche les retraits et profits significatifs (Social Proof).</Text>
                  </div>
                </div>
                <Form.Item name="marketing_enable_payout_tg" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <div className="flex justify-between items-center p-3 bg-slate-950/30 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/10 rounded-lg text-amber-400">
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <Text className="block font-bold">Signaux de Trading</Text>
                    <Text size="small" className="text-slate-500 text-xs">Permet l'envoi manuel de signaux via l'admin.</Text>
                  </div>
                </div>
                <Form.Item name="marketing_enable_signals_tg" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>

              <div className="flex justify-between items-center p-3 bg-slate-950/30 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-500/10 rounded-lg text-rose-400">
                    <AlertTriangle size={20} />
                  </div>
                  <div>
                    <Text className="block font-bold">Alertes Retraits Admin</Text>
                    <Text size="small" className="text-slate-500 text-xs">Notification immédiate pour chaque demande de retrait.</Text>
                  </div>
                </div>
                <Form.Item name="marketing_enable_withdraw_tg" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>
            </div>
          </Card>
        </div>

        {/* Global Save Button */}
        <div className="flex justify-end pt-4">
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large"
            loading={loading}
            icon={<Save size={20} />}
            className="btn-premium h-14 px-10 flex items-center gap-3 text-lg font-bold shadow-2xl"
          >
            Sauvegarder les Configurations
          </Button>
        </div>
      </Form>

      {/* === FORM 2: Signal de Trading (INDÉPENDANT) === */}
      <Card 
        title={<span className="flex items-center gap-2 text-amber-400"><TrendingUp size={18} /> Envoyer un Signal de Trading</span>}
        className="trading-card mb-8 mt-8"
      >
        <Form
          form={signalForm}
          layout="vertical"
          onFinish={handleSendSignal}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Form.Item name="pair" label="Paire (ex: BTC/USDT)" rules={[{ required: true, message: 'Requis' }]}>
              <Input placeholder="BTC/USDT" className="terminal-input" />
            </Form.Item>
            <Form.Item name="action" label="Action" rules={[{ required: true, message: 'Requis' }]}>
              <Input placeholder="BUY / LONG" className="terminal-input" />
            </Form.Item>
            <Form.Item name="entry" label="Prix d'entrée" rules={[{ required: true, message: 'Requis' }]}>
              <Input placeholder="65000" className="terminal-input" />
            </Form.Item>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="targets" label="Objectifs (séparés par des virgules)" rules={[{ required: true, message: 'Requis' }]}>
              <Input placeholder="67000, 69000, 72000" className="terminal-input" />
            </Form.Item>
            <Form.Item name="stoploss" label="Stop Loss" rules={[{ required: true, message: 'Requis' }]}>
              <Input placeholder="63000" className="terminal-input" />
            </Form.Item>
          </div>

          <div className="flex justify-end">
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={signalLoading}
              icon={<Send size={16} />}
              className="bg-amber-500 hover:bg-amber-400 border-none px-8"
            >
              Diffuser le Signal
            </Button>
          </div>
        </Form>
      </Card>

      {/* === FORM 3: Analyse de Marché (INDÉPENDANT) === */}
      <Card 
        title={<span className="flex items-center gap-2 text-indigo-400"><PieChart size={18} /> Diffuser une Analyse de Marché</span>}
        className="trading-card mb-8"
      >
        <Form
          form={analysisForm}
          layout="vertical"
          onFinish={handleSendAnalysis}
        >
          <Form.Item name="title" label="Titre de l'analyse" rules={[{ required: true, message: 'Requis' }]}>
            <Input placeholder="Analyse Technique BTC - 14 Mai" className="terminal-input" />
          </Form.Item>
          
          <Form.Item name="text" label="Contenu de l'analyse" rules={[{ required: true, message: 'Requis' }]}>
            <Input.TextArea rows={4} placeholder="Le BTC montre des signes de force au dessus du support..." className="terminal-input !bg-slate-950/50" />
          </Form.Item>

          <Form.Item name="image_url" label="URL de l'image / Graphique (Lien direct)" help="Hébergez votre image (ex: imgur) et collez le lien direct ici.">
            <Input placeholder="https://i.imgur.com/example.png" className="terminal-input" />
          </Form.Item>

          <div className="flex justify-end">
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={analysisLoading}
              icon={<Send size={16} />}
              className="bg-indigo-600 hover:bg-indigo-500 border-none px-8"
            >
              Envoyer l'Analyse
            </Button>
          </div>
        </Form>
      </Card>

      {/* Info Section */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-4"
      >
        <Alert
          message="Guide Marketing"
          description={
            <div className="text-xs text-slate-400 space-y-2 mt-2">
              <p>• <b>Social Proof :</b> L'activation des notifications de bienvenue et de paiement crée une ambiance "vivante" sur votre canal Telegram, encourageant les nouveaux à investir.</p>
              <p>• <b>Sécurité :</b> Ne partagez jamais votre Token Bot. Il donne un accès total à l'envoi de messages.</p>
              <p>• <b>Formatage :</b> Vous pouvez utiliser des tags HTML simples dans les messages Telegram.</p>
            </div>
          }
          type="info"
          showIcon
          className="bg-indigo-500/5 border-indigo-500/20"
        />
      </motion.div>
    </motion.div>
  );
};

export default MarketingManagement;
